import { useState } from "react";
import { motion } from "framer-motion";
import {
  Magnet,
  Download,
  ArrowUpRight,
  Copy,
  ArrowUp,
  ArrowDown,
  Skull,
} from "lucide-react";
import { torrentFileUrl } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { sizeToBytes, relativeDate, fmtNum } from "@/lib/format";

const CATEGORY_COLORS = {
  Movies: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  Series: "text-violet-300 border-violet-400/30 bg-violet-400/10",
  Anime: "text-pink-300 border-pink-400/30 bg-pink-400/10",
  Games: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  Apps: "text-orange-300 border-orange-400/30 bg-orange-400/10",
  Books: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  Music: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
  Porn: "text-red-300 border-red-400/30 bg-red-400/10",
  Other: "text-neutral-300 border-neutral-500/30 bg-neutral-500/10",
};

const COLUMNS = [
  { key: "name", label: "Name", align: "left" },
  { key: "size", label: "Size", align: "right" },
  { key: "seeders", label: "Seed", align: "right" },
  { key: "peers", label: "Peer", align: "right" },
  { key: "uploadDate", label: "Age", align: "right" },
];

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const active = sortBy === col.key;
  return (
    <th
      data-testid={`sort-${col.key}`}
      onClick={() => onSort(col.key)}
      className={`px-4 py-3 text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-500 select-none cursor-pointer hover:text-cyan-300 transition-colors ${
        col.align === "right" ? "text-right" : "text-left"
      } ${active ? "text-cyan-300" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {col.align === "right" && active && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        {col.label}
        {col.align === "left" && active && (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </span>
    </th>
  );
}

function ActionButton({ title, onClick, href, testId, children, danger }) {
  const cls =
    "p-2 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-cyan-400/60";
  if (href) {
    return (
      <a data-testid={testId} title={title} href={href} target="_blank" rel="noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button data-testid={testId} title={title} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

function Row({ t, index, onSelect, canDetails }) {
  const copyMagnet = () => {
    copyText(t.magnetUri, "Magnet link copied");
  };
  const copyDetails = () => {
    copyText(t.descriptionPageUrl, "Details link copied");
  };

  return (
    <motion.tr
      data-testid={`torrent-row-${index}`}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15, delay: Math.min(index * 0.012, 0.3) }}
      className="border-b border-neutral-800/80 hover:bg-neutral-800/40 group"
    >
      <td className="px-4 py-3 max-w-0 w-full">
        <div className="flex items-center gap-2">
          {t.isDead && <Skull size={13} className="shrink-0 text-neutral-600" title="Dead torrent" />}
          {t.isNSFW && (
            <span className="shrink-0 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
              NSFW
            </span>
          )}
          <span
            data-testid={canDetails ? `open-details-${index}` : `torrent-name-${index}`}
            onClick={canDetails ? () => onSelect(t) : undefined}
            title={t.name}
            className={`truncate text-[13px] text-neutral-100 transition-colors ${
              canDetails ? "cursor-pointer group-hover:text-cyan-300 hover:underline" : ""
            }`}
          >
            {t.name}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs text-neutral-300 whitespace-nowrap">
        {t.size || "—"}
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs font-bold text-emerald-400 whitespace-nowrap">
        {fmtNum(t.seeders)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs text-rose-400 whitespace-nowrap">
        {fmtNum(t.peers)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[11px] text-neutral-500 whitespace-nowrap">
        {relativeDate(t.uploadDate)}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {t.category && (
            <span
              className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                CATEGORY_COLORS[t.category] || CATEGORY_COLORS.Other
              }`}
            >
              {t.category}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-neutral-800 text-neutral-400 border border-neutral-700">
          {t.providerName}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center justify-end gap-0.5">
          <ActionButton testId={`copy-magnet-${index}`} title="Copy magnet link" onClick={copyMagnet}>
            <Copy size={15} />
          </ActionButton>
          <ActionButton testId={`open-magnet-${index}`} title="Open magnet" href={t.magnetUri}>
            <Magnet size={15} />
          </ActionButton>
          <ActionButton
            testId={`download-torrent-${index}`}
            title="Download .torrent"
            href={torrentFileUrl({ infoHash: t.infoHash, link: t.fileDownloadLink, name: t.name })}
          >
            <Download size={15} />
          </ActionButton>
          {t.descriptionPageUrl && (
            <ActionButton testId={`details-link-${index}`} title="Open details page" onClick={copyDetails}>
              <ArrowUpRight size={15} />
            </ActionButton>
          )}
        </div>
      </td>
    </motion.tr>
  );
}

export function ResultsTable({ torrents, onSelect, detailsProviders }) {
  const [sortBy, setSortBy] = useState("seeders");
  const [sortDir, setSortDir] = useState("desc");

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sorted = [...torrents].sort((a, b) => {
    let av, bv;
    switch (sortBy) {
      case "name":
        av = a.name.toLowerCase();
        bv = b.name.toLowerCase();
        break;
      case "size":
        av = sizeToBytes(a.size);
        bv = sizeToBytes(b.size);
        break;
      case "uploadDate":
        av = a.uploadDate ? new Date(a.uploadDate).getTime() : 0;
        bv = b.uploadDate ? new Date(b.uploadDate).getTime() : 0;
        break;
      case "peers":
        av = a.peers || 0;
        bv = b.peers || 0;
        break;
      default:
        av = a.seeders || 0;
        bv = b.seeders || 0;
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-900/40">
      <table className="w-full border-collapse" data-testid="results-table">
        <thead className="sticky top-0 z-10 bg-neutral-950/95 backdrop-blur border-b border-neutral-800">
          <tr>
            {COLUMNS.map((c) => (
              <SortHeader key={c.key} col={c} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            ))}
            <th className="px-3 py-3 text-left text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-500">
              Cat
            </th>
            <th className="px-3 py-3 text-left text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-500">
              Source
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((t, i) => (
            <Row
              key={`${t.infoHash}-${i}`}
              t={t}
              index={i}
              onSelect={onSelect}
              canDetails={detailsProviders?.has(t.providerName)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
